import { Link } from "react-router-dom";
import type { WorkflowStepRendererProps } from "../types";
import type { VoiceReviewSnapshot } from "../step-modules/voice-review";

export function VoiceReviewStepRenderer({
  context,
  state,
}: WorkflowStepRendererProps<VoiceReviewSnapshot>) {
  if (!state.runId) return null;
  const panelTitle = context.brief.state === "voice_review" ? "Voice review" : "Last voice review";
  const panelDescription = state.isLoading
    ? "Loading voice review."
    : state.status === "running"
      ? "Scoring the draft against the brand voice."
      : context.brief.state === "voice_review"
        ? state.snapshot?.summary ?? "Voice review recorded."
        : state.snapshot?.summary ?? "The latest voice review is recorded here for approval context.";

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-3 text-sm text-violet-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold">{panelTitle}</p>
          <p className="mt-1 text-xs text-violet-900/80">{panelDescription}</p>
        </div>
        <Link
          to={`/runs/${encodeURIComponent(state.runId)}`}
          className="text-xs font-medium text-violet-700 underline underline-offset-2"
        >
          View run
        </Link>
      </div>
      {(state.isLoading || state.status === "running") && (
        <div className="mt-3 flex items-center gap-2 text-xs text-violet-800">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
          {state.isLoading ? "Loading voice review" : "Voice review in progress"}
        </div>
      )}
      {state.error && !state.run && (
        <p className="mt-3 text-xs text-red-700">{state.error.message}</p>
      )}
      {state.run?.status === "failed" && (
        <p className="mt-3 text-xs text-red-700">
          {state.run.error ?? "Voice review failed."}
        </p>
      )}
      {state.snapshot && state.run?.status === "completed" && (
        <div className="mt-3 space-y-2 text-xs text-violet-950">
          {(state.snapshot.fromState || state.snapshot.toState) && (
            <p>
              <span className="font-semibold">State transition:</span>{" "}
              {state.snapshot.fromState ?? "unknown"} → {state.snapshot.toState ?? "unknown"}
            </p>
          )}
          {state.snapshot.documentKey && (
            <p>
              <span className="font-semibold">Review document:</span> {state.snapshot.documentKey}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
